import { Component, computed, DestroyRef, effect, signal } from "@angular/core";
import { CommonModule, NgClass } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { RouterModule, NavigationEnd, Router } from "@angular/router";
import { AuthPolicy } from "../../enums/auth-policy";
import { AuthService } from "../../services/auth.service";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconService } from "../../services/icon.service";
import { SystemLogoComponent } from "../sveaskog-logo/sveaskog-logo.component";
import { environment } from "../../../../environments/environment";
import { BreakpointObserver } from "@angular/cdk/layout";
import { MatButtonModule } from "@angular/material/button";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { map } from "rxjs/operators";

@Component({
  selector: 'app-main-menu',
  imports: [RouterModule, MatSidenavModule, MatIconModule, MatToolbarModule, MatListModule, FontAwesomeModule, SystemLogoComponent, NgClass, MatButtonModule, CommonModule],
  templateUrl: './main-menu.component.html',
  styleUrl: './main-menu.component.scss'
})
export class MainMenuComponent {
  readonly homeRoute = '/';
  readonly machinesRoute = "/machines";
  readonly teamsRoute = "/teams";
  readonly definitionValuesRoute = "/definitions"
  readonly contractorsRoute = "/contractors"
  readonly machinePerformancesRoute = "/machine-performances"
  readonly userRoute = "/users"
  readonly settingsRoute = "/settings";
  readonly environmentLabel = environment.environmentName;
  readonly showEnvironmentLabel = !environment.production;
  readonly environmentBadgeClass = this.showEnvironmentLabel
    ? `environment-${environment.environmentName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    : null;

  activeRoute!: string;

  // Method to get the appropriate icon based on route using IconService
  getIcon(route: string, iconType: 'home' | 'machines' | 'teams' | 'definitions' | 'contractors' | 'users' | 'performances' | 'settings') {
    const isActive = this.activeRoute === route;

    switch (iconType) {
      case 'machines':
        return this.iconService.getIcon('customMachine', isActive);
      case 'teams':
        return this.iconService.getIcon('houseFlag', isActive);
      case 'definitions':
        return this.iconService.getIcon('clipboard', isActive);
      case 'contractors':
        return this.iconService.getIcon('rectangleHistory', isActive);
      case 'users':
        return this.iconService.getIcon('users', isActive);
      case 'performances':
        return this.iconService.getIcon('chart', isActive);
      case 'settings':
        return this.iconService.getIcon('circleUser', isActive);
      case 'home':
        return this.iconService.getIcon('home', isActive);
      default:
        return this.iconService.getIcon('home', false);
    }
  }

  isPolicyDeveloper: boolean = false;
  isPolicyPTAdmin: boolean = false;
  isPolicyPT: Boolean = false;
  private readonly isMobileMatch = signal(false);
  readonly isMobile = computed(() => this.isMobileMatch());
  readonly isMobileMenuOpen = signal(false);


  constructor(
    private router: Router,
    public authService: AuthService,
    private iconService: IconService,
    private breakpointObserver: BreakpointObserver,
    private readonly destroyRef: DestroyRef
  ) {
    this.breakpointObserver.observe(['(max-width: 959.98px)', '(max-height: 550px)'])
      .pipe(
        map(state => state.matches),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(isMobile => this.isMobileMatch.set(isMobile));

    effect(() => {
      if (!this.isMobile()) {
        this.isMobileMenuOpen.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.loadInitialActiveRoute();

    if (this.authService.isPolicy(AuthPolicy.Developer)) {
      this.isPolicyDeveloper = true;
    }

    if (this.authService.isPolicy(AuthPolicy.PTAdmin)) {
      this.isPolicyPTAdmin = true;
    }

    if (this.authService.isPolicy(AuthPolicy.PT)) {
      this.isPolicyPT = true;
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (this.routeHasParent(event.url)) {
          return;
        }

        if (event.url.includes("/settings/")) {
          this.activeRoute = this.settingsRoute;
          this.closeMobileMenu();
          return;
        }

        this.activeRoute = event.url;
        this.closeMobileMenu();
      }
    });
  }

  private loadInitialActiveRoute() {
    if (this.routeHasParent(this.router.url)) {
      return;
    }

    this.activeRoute = this.router.url;
  }

  routeHasParent(url: string) {
    if (url.includes("/machine/")) {
      this.activeRoute = this.machinesRoute;
      this.closeMobileMenu();
      return true;
    }

    if (url.includes("/team/")) {
      this.activeRoute = this.teamsRoute;
      this.closeMobileMenu();
      return true;
    }

    if (url.includes("/contractor/")) {
      this.activeRoute = this.contractorsRoute;
      this.closeMobileMenu();
      return true;
    }

    return false;
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen.update(open => !open);
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
  }

  onMenuItemSelected(): void {
    if (this.isMobile()) {
      this.closeMobileMenu();
    }
  }

}
